import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ComplaintsSection } from "@/components/admin/ComplaintsSection";
import { ComplaintDetailDialog } from "@/components/admin/ComplaintDetailDialog";
import { MediaPreviewDialog } from "@/components/admin/MediaPreviewDialog";
import { StatsCards } from "@/components/admin/StatsCards";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { DEFAULT_FILTERS } from "@/lib/constants";
import { cn } from "@/lib/utils";

const buildParams = (filters) => {
  const params = {};
  if (filters.jenis !== "all") params.jenis = filters.jenis;
  if (filters.status !== "all") params.status = filters.status;
  if (filters.q) params.q = filters.q;
  if (filters.from) params.date_from = filters.from;
  if (filters.to) params.date_to = filters.to;
  return params;
};

const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export default function AdminDashboardPage() {
  const { logout } = useAuth();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [complaints, setComplaints] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [preview, setPreview] = useState({ items: [], index: null, returnTo: null });
  const [detailId, setDetailId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const handleError = useCallback((err, fallback) => {
    if (err.response?.status === 401) {
      toast.error("Sesi berakhir, silakan login kembali");
      return logout();
    }
    toast.error(formatApiError(err, fallback));
  }, [logout]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = buildParams(filters);
    try {
      const [list, st] = await Promise.all([api.get("/admin/complaints", { params }), api.get("/admin/stats")]);
      setComplaints(list.data);
      setStats(st.data);
      setLoadError(false);
    } catch (err) {
      setLoadError(true);
      handleError(err, "Gagal memuat data pengaduan");
    } finally {
      setLoading(false);
    }
  }, [filters, handleError]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      const { data } = await api.patch(`/admin/complaints/${id}/status`, { status });
      const hide = filters.status !== "all" && data.status !== filters.status;
      setComplaints((prev) => (hide ? prev.filter((c) => c.id !== id) : prev.map((c) => (c.id === id ? data : c))));
      const st = await api.get("/admin/stats");
      setStats(st.data);
      toast.success(`Status diubah menjadi "${status}"`, { description: `Tiket #${data.ticket}` });
    } catch (err) {
      handleError(err, "Gagal mengubah status");
    } finally {
      setUpdatingId(null);
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/export/complaints", { params: buildParams(filters), responseType: "blob", timeout: 60000 });
      saveBlob(res.data, `pengaduan-rt-merak-${format(new Date(), "yyyyMMdd-HHmm")}.xlsx`);
      toast.success("File Excel berhasil diunduh", { description: "Isi file mengikuti filter yang sedang aktif." });
    } catch (err) {
      handleError(err, "Gagal mengunduh file Excel");
    } finally {
      setExporting(false);
    }
  };

  const openMedia = (items, index) => {
    setPreview({ items, index, returnTo: detailId });
    setDetailId(null);
  };

  const closeMedia = () => {
    setDetailId(preview.returnTo);
    setPreview({ items: [], index: null, returnTo: null });
  };

  const replaceComplaint = useCallback((updated) => {
    setComplaints((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
  }, []);

  const handleProgressAdded = useCallback(async (updated) => {
    replaceComplaint(updated);
    try {
      const st = await api.get("/admin/stats");
      setStats(st.data);
    } catch {
      /* stats refresh is best-effort */
    }
  }, [replaceComplaint]);

  return (
    <main className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8" data-testid="admin-dashboard-page">
      <div className="fade-up flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Panel Pengurus</p>
          <h2 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl" data-testid="dashboard-title">Dashboard Admin RT</h2>
          <p className="mt-1 text-sm text-slate-500">Pantau dan tindak lanjuti pengaduan warga Cluster Merak.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} className="rounded-xl" data-testid="refresh-btn">
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} /> Muat ulang
          </Button>
          <Button onClick={logout} className="rounded-xl bg-slate-900 hover:bg-slate-800" data-testid="logout-btn">
            <LogOut className="h-4 w-4" /> Keluar
          </Button>
        </div>
      </div>
      <StatsCards stats={stats} />
      <ComplaintsSection
        complaints={complaints}
        loading={loading}
        filters={filters}
        onFiltersChange={setFilters}
        onExport={exportExcel}
        loadError={loadError}
        onRetry={load}
        exporting={exporting}
        onStatusChange={changeStatus}
        updatingId={updatingId}
        onOpenMedia={openMedia}
        onOpenDetail={(c) => setDetailId(c.id)}
      />
      <ComplaintDetailDialog
        complaintId={detailId}
        onClose={() => setDetailId(null)}
        onOpenMedia={openMedia}
        onComplaintChange={replaceComplaint}
        onProgressAdded={handleProgressAdded}
      />
      <MediaPreviewDialog
        items={preview.items}
        index={preview.index}
        onIndexChange={(index) => setPreview((p) => ({ ...p, index }))}
        onClose={closeMedia}
      />
    </main>
  );
}
